import { CategoryV2 } from './../components/generics/data-printer-v2/data-printer-v2.component';
import { PersonType } from './person-type.model';
import { LegalPerson } from './legal-person.model';
import { PhysicalPerson } from './physical-person.model';
import { Category } from '../components/generics/data-printer/data-printer.component';
import { ID } from './ID.model';
import { Representative } from './representative.model';

export interface Tenant {
    id?: ID;
    email: string; // Email de la persona
    tipoPersona: PersonType;
    personaFisica?: PhysicalPerson;
    personaJuridica?: LegalPerson;
}


export function getTenantName(tenant: Tenant) {
    if (tenant?.tipoPersona === 'FISICA' && tenant.personaFisica?.nombre)
        return tenant.personaFisica.nombre + ' ' + tenant.personaFisica.apellido;
    else if (tenant?.tipoPersona === 'JURIDICA' && tenant.personaJuridica?.razonSocial)
        return tenant.personaJuridica.razonSocial;
    else
        return '';
}

export function getTenantPerson(tenant: Tenant): PhysicalPerson | LegalPerson {
    return tenant.tipoPersona === 'FISICA' ? tenant.personaFisica : tenant.personaJuridica;
}

export function generateTenantCategories(tenant: Tenant, generalSectionName: string, addressSectionName: string | undefined, representativeSectionName: string) {
    if (!tenant) return [];
    const person = getTenantPerson(tenant);
    const categories: Category[] = [
        { name: generalSectionName, items: [
            { label: 'Tipo de persona', value: tenant.tipoPersona },
            { label: 'Teléfono', value: person.celular, type: 'phone' },
            { label: 'E-Mail', value: tenant.email, type: 'email' },
        ]}
    ];
    if (tenant.tipoPersona === 'JURIDICA') {
        if (addressSectionName) {
            categories.push({ name: addressSectionName, items: [
                { label: 'Tipo de vía', value: person.direccion?.tipoCalle },
                { label: 'Calle', value: person.direccion?.calle },
                { label: 'Número', value: person.direccion?.numero },
                { label: 'Piso', value: person.direccion?.piso },
                { label: 'Departamento', value: person.direccion?.departamento },
                { label: 'Código postal', value: person.direccion?.codigoPostal },
                { label: 'Provincia', value: person.direccion?.localidad?.provincia?.descripcion },
                { label: 'Municipio', value: person.direccion?.localidad?.municipio?.descripcion },
                { label: 'Localidad', value: person.direccion?.localidad?.descripcion },
            ]});
        }
        categories[0].items.push(
            { label: 'Razón social', value: tenant.personaJuridica?.razonSocial },
            { label: 'CUIT', value: tenant.personaJuridica?.cuit, type: 'cuit' },
            { label: 'Rubro', value: tenant.personaJuridica?.rubro?.descripcion },
            { label: 'Situación frente al IVA', value: tenant.personaJuridica?.situacionIVA?.descripcion },
            { label: 'Tipo de sociedad', value: tenant.personaJuridica?.tipoSociedad },
        );
    } else {
        categories[0].items.push(
            { label: 'Nombre', value: tenant.personaFisica?.nombre },
            { label: 'Apellido', value: tenant.personaFisica?.apellido },
            { label: 'DNI', value: tenant.personaFisica?.dni },
            { label: 'Pasaporte', value: tenant.personaFisica?.pasaporte },
            { label: 'Sexo', value: tenant.personaFisica?.sexo },
            { label: 'Fecha de nacimiento', value: tenant.personaFisica?.fechaNacimiento, type: 'date' },
        );
    }
    if (person.representante) {
        const category: Category = { name: representativeSectionName, items: [
            { label: 'Nombre', value: person.representante.nombre },
            { label: 'Apellido', value: person.representante.apellido },
            { label: 'Fecha de nacimiento', value: person.representante.fechaNacimiento, type: 'date' },
            { label: 'E-Mail', value: person.representante.mail, type: 'email' },
            { label: 'Teléfono', value: person.representante.celular, type: 'phone' },
            { label: 'Cargo', value: person.representante.cargo },
            { label: 'DNI', value: person.representante.dni },
            { label: 'Dirección', value: person.representante.direccion, type: 'address' },
        ]};
        if (person.representante.cargo === 'APODERADO') {
            category.items.push(
                { label: 'Fecha del poder', value: person.representante.fechaPoder, type: 'date' },
            );
        } else {
            category.items.push(
                { label: 'Nombre del cargo', value: person.representante.nombreCargo },
                { label: 'Nro acta asamblea/estatuto', value: person.representante.nroAsambleaEstatuto },
                { label: 'Fecha del acta', value: person.representante.fechaPoder, type: 'date' },
            );
        }
        categories.push(category);
    }
    return categories;
}

export function generateTenantCategoriesV2(tenant?: Tenant) {
    if (!tenant) return [];

    const categories: CategoryV2[] = [];
    let representante: Representative | undefined;

    if (tenant.tipoPersona === 'FISICA' && tenant.personaFisica) {
        // Información general
        categories.push({
            labelWidth: '145px',
            minWidth: '380px',
            maxWidth: '450px',
            name: 'Información general',
            items: [
                { label: 'E-Mail', value: tenant.email, type: 'email' },
                { label: 'Tipo de persona', value: tenant.tipoPersona, type: 'text' },
                { label: 'Teléfono', value: tenant.personaFisica.celular, type: 'phone' },
                { label: 'Nombre', value: tenant.personaFisica.nombre, type: 'text' },
                { label: 'Apellido', value: tenant.personaFisica.apellido, type: 'text' },
                { label: 'DNI', value: tenant.personaFisica.dni, type: 'text' },
                { label: 'Pasaporte', value: tenant.personaFisica.pasaporte, type: 'text' }, // NUEVO
                { label: 'Sexo', value: tenant.personaFisica.sexo, type: 'text' },
                { label: 'Fecha de nacimiento', value: tenant.personaFisica.fechaNacimiento, type: 'date' },
            ]
        });
        representante = tenant.personaFisica.representante;
    } else if (tenant.tipoPersona === 'JURIDICA' && tenant.personaJuridica) {
        // Información general
        categories.push({
            labelWidth: '145px',
            minWidth: '380px',
            maxWidth: '500px',
            name: 'Información general',
            items: [
                { label: 'E-Mail', value: tenant.email, type: 'email' },
                { label: 'Tipo de persona', value: tenant.tipoPersona, type: 'text' },
                { label: 'Teléfono', value: tenant.personaJuridica.celular, type: 'phone' },
                { label: 'Razón social', value: tenant.personaJuridica.razonSocial, type: 'text' },
                { label: 'CUIT', value: tenant.personaJuridica.cuit, type: 'cuit' },
                { label: 'Rubro', value: tenant.personaJuridica.rubro?.descripcion, type: 'text' },
                { label: 'Situación IVA', value: tenant.personaJuridica.situacionIVA?.descripcion, type: 'text' },
                { label: 'Tipo de sociedad', value: tenant.personaJuridica.tipoSociedad, type: 'text' },
            ]
        });
        representante = tenant.personaJuridica.representante;
    }
    if ((tenant.tipoPersona === 'FISICA' && tenant.personaFisica?.direccion) || (tenant.tipoPersona === 'JURIDICA' && tenant.personaJuridica?.direccion)) {
        // Dirección
        categories.push({
            maxWidth: '500px',
            name: 'Dirección',
            items: [
                { value: tenant.tipoPersona === 'FISICA' ? tenant.personaFisica?.direccion : tenant.personaJuridica?.direccion, type: 'address', alignment: 'center' },
            ]
        });
    }
    if (representante) {
        const categoriaRepresentante: CategoryV2 = {
            labelWidth: '190px',
            minWidth: '380px',
            maxWidth: '500px',
            name: 'Datos del representante',
            items: [
                { label: 'E-Mail', value: representante.mail, type: 'email' },
                { label: 'Nombre', value: representante.nombre, type: 'text' },
                { label: 'Apellido', value: representante.apellido, type: 'text' },
                { label: 'Fecha de nacimiento', value: representante.fechaNacimiento, type: 'date' },
                { label: 'Teléfono', value: representante.celular, type: 'phone' },
                { label: 'Cargo', value: representante.cargo, type: 'text' },
            ]
        };
        if (representante.cargo === 'APODERADO') {
            categoriaRepresentante.items.push(
                { label: 'Fecha del poder', value: representante.fechaPoder, type: 'date' },
            );
        } else {
            categoriaRepresentante.items.push(
                { label: 'Nombre del cargo', value: representante.nombreCargo },
                { label: 'Nro acta asamblea/estatuto', value: representante.nroAsambleaEstatuto },
                { label: 'Fecha del acta', value: representante.fechaPoder, type: 'date' },
            );
        }
        categories.push(categoriaRepresentante);
    }

    return categories;
}

